import { Euler, Group, type Scene, type WebGLRenderer } from "three";
import { SparkRenderer, SplatMesh } from "@sparkjsdev/spark";
import type { Scene as SceneJson } from "../data/types";

/**
 * A Gaussian Splat backdrop (e.g. Arena.ply) for free-look mode.
 *
 * Construction is lazy — we wait until the user actually enters free-look
 * before fetching the (potentially 100s of MB) splat file. Once loaded
 * the same SplatMesh stays around; we just toggle visibility on the
 * containing Group on each mode swap.
 *
 * Why free-look only: in snap-mode the source mp4 IS the backdrop, so
 * drawing the splat there is wasted bandwidth + overdraw. It would also
 * stack two semi-transparent layers (the splat and the mp4) which looks
 * muddy.
 *
 * Compatibility: spark.js requires three.js >= 0.180 (peer dep), which
 * is why `package.json` was bumped from 0.169.
 */
export interface SplatBackdrop {
  group: Group;
  /** Free-look toggles this on; snap mode toggles it off. */
  setVisible(v: boolean): void;
  /** Force-load now (otherwise loads on first setVisible(true)). */
  load(): void;
  /** Tune placement live (used by the alignment UI). All in metres / Y-up. */
  setTransform(opts: {
    position?: [number, number, number];
    rotation_euler?: [number, number, number];
    scale?: number | [number, number, number];
  }): void;
}

/**
 * Build (lazily) the splat backdrop from `sceneJson.splat`. Returns null
 * if the scene has no splat configured.
 *
 * The SparkRenderer must be added to the three.js scene tree alongside
 * the SplatMesh — it's the runtime that drives the splat shader. We attach
 * a single shared SparkRenderer per scene.
 */
export function makeSplatBackdrop(
  sceneJson: SceneJson,
  renderer: WebGLRenderer,
  scene: Scene,
): SplatBackdrop | null {
  if (!sceneJson.splat) return null;

  const group = new Group();
  group.name = "splatBackdrop";
  group.visible = false;          // free-look will turn it on
  scene.add(group);

  // Single SparkRenderer per WebGLRenderer; attaching to the scene root is
  // the documented pattern (see sparkjs.dev/docs/getting-started).
  const spark = new SparkRenderer({ renderer });
  scene.add(spark);

  let mesh: SplatMesh | null = null;
  let loadStarted = false;

  function applyTransform(
    target: SplatMesh,
    pos?: [number, number, number],
    rot?: [number, number, number],
    scl?: number | [number, number, number],
  ): void {
    if (pos) target.position.set(pos[0], pos[1], pos[2]);
    if (rot) target.quaternion.setFromEuler(new Euler(rot[0], rot[1], rot[2], "XYZ"));
    if (scl != null) {
      if (typeof scl === "number") target.scale.setScalar(scl);
      else target.scale.set(scl[0], scl[1], scl[2]);
    }
  }

  // Cached transform: when the mesh hasn't loaded yet, store the latest
  // setTransform() call and apply it once the file arrives.
  const pending = {
    position: sceneJson.splat.position,
    rotation_euler: sceneJson.splat.rotation_euler,
    scale: sceneJson.splat.scale,
  };

  function startLoad(): void {
    if (loadStarted || !sceneJson.splat) return;
    loadStarted = true;
    // Resolve to an ABSOLUTE URL against the page origin. spark fetches the
    // splat from inside a Web Worker, whose base URL is the worker script (a
    // bundled blob), NOT the page — so a root-relative "/splats/Arena.spz"
    // resolves against the wrong base there and the fetch fails. Absolutizing
    // here makes the worker fetch the correct origin. Handles both
    // "/relative" and already-absolute "https://…" forms.
    const splatUrl = new URL(sceneJson.splat.url, location.origin).href;
    console.info(`splat: fetching ${splatUrl}`);
    const m = new SplatMesh({
      url: splatUrl,
      onLoad: () => {
        console.info("splat: loaded");
      },
    });
    applyTransform(m, pending.position, pending.rotation_euler, pending.scale);
    group.add(m);
    mesh = m;
  }

  return {
    group,
    setVisible(v: boolean): void {
      if (v && !loadStarted) startLoad();
      group.visible = v;
    },
    load: startLoad,
    setTransform(opts) {
      if (opts.position) pending.position = opts.position;
      if (opts.rotation_euler) pending.rotation_euler = opts.rotation_euler;
      if (opts.scale != null) pending.scale = opts.scale;
      if (mesh) {
        applyTransform(mesh, opts.position, opts.rotation_euler, opts.scale);
      }
    },
  };
}
